import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Circle, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { useAuth } from "../context/AuthContext";

const ITEMS = [
  { k: "name", l: "Nombre completo", ok: (u) => (u.name || "").trim().split(/\s+/).length >= 2 },
  { k: "specialty", l: "Especialidad", ok: (u) => !!(u.specialty || "").trim() },
  { k: "cedula", l: "Cédula profesional", ok: (u) => !!(u.cedula || "").trim() },
  { k: "clinic_name", l: "Nombre del consultorio", ok: (u) => !!(u.clinic_name || "").trim() },
  { k: "address", l: "Dirección del consultorio", ok: (u) => !!(u.address || "").trim() },
  { k: "phone", l: "Teléfono de contacto", ok: (u) => !!(u.phone || "").trim() },
  { k: "signature", l: "Firma electrónica", ok: (u) => !!u.signature },
  { k: "photo", l: "Foto de perfil", ok: (u) => !!(u.photo || u.picture) },
];

export const profileProgress = (u) => {
  const rows = ITEMS.map((i) => ({ ...i, done: !!u && i.ok(u) }));
  const done = rows.filter((r) => r.done).length;
  return { rows, pct: Math.round((done / rows.length) * 100), missing: rows.length - done };
};

const Ring = ({ pct }) => {
  const r = 34, c = 2 * Math.PI * r;
  return (
    <div className="relative w-24 h-24 shrink-0" data-testid="profile-reminder-pct">
      <svg viewBox="0 0 80 80" className="w-24 h-24 -rotate-90">
        <circle cx="40" cy="40" r={r} fill="none" stroke="#E2E8F0" strokeWidth="7" />
        <motion.circle cx="40" cy="40" r={r} fill="none" stroke="#00B4CB" strokeWidth="7" strokeLinecap="round"
          strokeDasharray={c} initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: c * (1 - pct / 100) }} transition={{ duration: 1, ease: "easeOut" }} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-outfit font-extrabold text-xl text-brand-ink">{pct}%</span>
    </div>
  );
};

export default function ProfileReminder() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const prog = profileProgress(user);
  const token = localStorage.getItem("session_token") || "s";
  const seenKey = "clinped_profile_reminder_seen";

  useEffect(() => {
    if (!user || prog.missing === 0 || location.pathname === "/perfil") return;
    if (localStorage.getItem(seenKey) === token || !localStorage.getItem("oci_tour_done")) return;
    const t = setTimeout(() => { setOpen(true); localStorage.setItem(seenKey, token); }, 1200);
    return () => clearTimeout(t);
  }, [user, prog.missing, location.pathname, token]);

  const go = () => { setOpen(false); navigate("/perfil"); };
  const words = (user?.name || "").trim().split(/\s+/).filter(Boolean);
  const first = /^dra?\.?$/i.test(words[0] || "") ? `${words[0]} ${words[1] || ""}`.trim() : `Dr(a). ${words[0] || ""}`.trim();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="profile-reminder" className="rounded-3xl max-w-md p-0 overflow-hidden border-0 [&>button]:text-white [&>button]:opacity-90 [&>button]:z-10">
        <div className="bg-[#0D47A1] text-white px-6 pt-6 pb-5 relative overflow-hidden">
          <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-[#00B4CB]/30" />
          <div className="flex items-center gap-3 relative">
            <img src="/oci/wave.png" alt="Oci" className="w-14 h-14 rounded-full bg-white object-cover border-2 border-white" />
            <div>
              <DialogTitle className="font-outfit font-bold text-xl text-white">{/^dra\b/i.test(first) ? "Estimada" : "Estimado"} {first}</DialogTitle>
              <p className="text-sky-100 text-xs">Una sugerencia amable de Oci</p>
            </div>
          </div>
        </div>
        <div className="px-6 pt-5 pb-6">
          <div className="flex items-center gap-4">
            <Ring pct={prog.pct} />
            <DialogDescription className="text-sm text-brand-ink/80 leading-relaxed">
              Recuerda completar tu perfil para que disfrutes de forma óptima de todos los beneficios de ClinPed: recetas con tu firma, correos a pacientes con tu consultorio y mejor visibilidad. ¡Gracias!
            </DialogDescription>
          </div>
          <ul className="mt-5 grid grid-cols-2 gap-x-3 gap-y-2" data-testid="profile-reminder-list">
            {prog.rows.map((r) => (
              <li key={r.k} data-testid={`profile-item-${r.k}`} className={`flex items-center gap-1.5 text-xs ${r.done ? "text-muted-foreground line-through" : "text-brand-ink font-semibold"}`}>
                {r.done ? <CheckCircle2 size={14} className="text-emerald-500 shrink-0" /> : <Circle size={14} className="text-[#F59E0B] shrink-0" />} {r.l}
              </li>
            ))}
          </ul>
          <div className="flex gap-2.5 mt-6">
            <button data-testid="profile-reminder-later" onClick={() => setOpen(false)}
              className="flex-1 py-3 rounded-2xl text-sm font-semibold text-brand-ink/70 bg-stone-100 hover:bg-stone-200 transition-colors">Más tarde</button>
            <button data-testid="profile-reminder-go" onClick={go}
              className="flex-[1.4] flex items-center justify-center gap-1.5 py-3 rounded-2xl text-sm font-semibold text-white bg-[#0D47A1] hover:bg-[#0B3C8A] transition-colors">
              Completar ahora <ArrowRight size={16} /></button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
